import { useState } from "react";
import { Link } from "react-router-dom";
import { PlayCircle, MessageSquare, Users, ArrowRight, Loader2, CheckCircle2, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PageShell, Section } from "@/components/PageShell";

const linkCard =
  "flex items-center gap-3 rounded-2xl border border-white/10 bg-[#14213D] px-5 py-4 font-heading text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5";

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [msg, setMsg] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
    if (!form.name.trim() || !emailValid || !form.message.trim()) {
      setStatus("error");
      setMsg("Please add your name, a valid email, and a message.");
      return;
    }
    setStatus("loading");
    setMsg("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/support/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      const json = await res.json();
      if (json && json.sent === false) {
        setStatus("error");
        setMsg("We could not send your message right now. Please email support@commandcenteradhd.com directly.");
        return;
      }
      setStatus("done");
    } catch (err) {
      setStatus("error");
      setMsg("Something went wrong sending your message. Please email support@commandcenteradhd.com.");
    }
  };

  const reset = () => {
    setForm({ name: "", email: "", message: "" });
    setStatus("idle");
    setMsg("");
  };

  if (status === "done") {
    const firstName = form.name.trim().split(" ")[0];
    return (
      <div
        data-testid="contact-success"
        className="max-w-[520px] overflow-hidden rounded-3xl border border-[#F5A623]/30 bg-gradient-to-b from-[#16294a] to-[#111d36] px-7 py-8 text-center shadow-[0_20px_50px_rgba(0,0,0,0.35)] duration-500 animate-in fade-in zoom-in-95"
      >
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#F5A623]/15 ring-8 ring-[#F5A623]/5">
          <CheckCircle2 className="h-9 w-9 text-[#F5A623] duration-700 animate-in zoom-in-50" />
        </div>
        <h3 className="mt-5 font-heading text-2xl font-extrabold text-white">
          {firstName ? `Thanks, ${firstName}!` : "Message sent!"}
        </h3>
        <p className="mx-auto mt-3 max-w-[400px] text-[15px] leading-relaxed text-[#BFD4E8]">
          Your message is on its way. We reply to every note personally, usually
          within one business day, at the email you gave us.
        </p>
        <button
          type="button"
          onClick={reset}
          data-testid="contact-send-another-button"
          className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 px-5 py-3 font-heading text-sm font-bold text-white transition-colors duration-200 hover:border-[#F5A623] hover:text-[#F5A623]"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="max-w-[520px] space-y-4" data-testid="contact-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="sr-only">Your name</label>
          <Input
            id="contact-name"
            type="text"
            placeholder="Your name"
            value={form.name}
            onChange={set("name")}
            data-testid="contact-name-input"
            className="h-12 border-white/15 bg-[#14213D] text-base text-white placeholder:text-[#6E829B]"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="sr-only">Email address</label>
          <Input
            id="contact-email"
            type="email"
            inputMode="email"
            placeholder="name@email.com"
            value={form.email}
            onChange={set("email")}
            data-testid="contact-email-input"
            className="h-12 border-white/15 bg-[#14213D] text-base text-white placeholder:text-[#6E829B]"
          />
        </div>
      </div>
      <div>
        <label htmlFor="contact-message" className="sr-only">Message</label>
        <textarea
          id="contact-message"
          rows={5}
          placeholder="How can we help?"
          value={form.message}
          onChange={set("message")}
          data-testid="contact-message-input"
          className="w-full rounded-xl border border-white/15 bg-[#14213D] px-4 py-3 text-base text-white placeholder:text-[#6E829B] focus:outline-none focus:ring-2 focus:ring-[#F5A623]"
        />
      </div>
      {status === "error" && (
        <p className="text-sm font-medium text-[#FF8A80]" data-testid="contact-error">{msg}</p>
      )}
      <button
        type="submit"
        disabled={status === "loading"}
        data-testid="contact-submit-button"
        className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#F5A623] px-6 py-3.5 font-heading text-base font-bold text-[#0B132B] transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-70"
      >
        {status === "loading" ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Send message <Send className="h-4 w-4" /></>}
      </button>
    </form>
  );
};

export default function About() {
  return (
    <PageShell
      title="About CommandCenterADHD"
      subtitle="A Notion based planning system for choosing what matters, organizing tasks around available energy, and restarting after interruptions."
      testid="about-page"
    >
      <Section heading="Product purpose">
        <p>
          CommandCenterADHD is a Notion based ADHD planning system for choosing
          what matters, organizing tasks around your energy, and restarting after
          interruptions. It helps people manage daily responsibilities without
          turning planning into another complicated responsibility. It brings
          tasks, priorities, projects, daily journals, and reset actions into
          one Notion workspace.
        </p>
      </Section>

      <Section heading="Mission">
        <p>
          CommandCenterADHD provides a practical Notion based planning system
          that helps people with ADHD capture tasks, choose one priority, plan
          around their available energy, and restart after a disrupted day.
        </p>
      </Section>

      <Section heading="Vision">
        <p>
          We want people with ADHD to have planning tools that provide clear
          next steps, respect changing capacity, and make it easier to return
          after an unfinished or difficult day.
        </p>
      </Section>

      <Section heading="What the product is">
        <p>
          CommandCenterADHD is a digital Notion template. It is not a standalone
          application, medical service, or ADHD treatment.
        </p>
      </Section>

      <Section heading="Who it is for">
        <p>
          It is intended for people who want a clearer way to capture
          responsibilities, choose what deserves attention, and return to their
          plan after interruptions or difficult days.
        </p>
      </Section>

      <Section heading="Who built this" id="founder">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
          <img
            src="/founder-tunde.jpg"
            alt="Tunde Sobowale, founder of CommandCenterADHD"
            width="160"
            height="200"
            loading="lazy"
            className="h-[200px] w-[160px] flex-shrink-0 rounded-2xl border border-white/10 object-cover object-top shadow-[0_12px_30px_rgba(0,0,0,0.35)]"
            data-testid="founder-photo"
          />
          <div className="flex-1">
            <p className="font-heading text-lg font-extrabold text-white" data-testid="founder-name">
              Tunde Sobowale
            </p>
            <p className="mt-1 text-sm font-semibold text-[#A8D5F5]">Founder, CommandCenterADHD</p>
            <p className="mt-4">
              I am Tunde, and I built CommandCenterADHD because I live with ADHD
              and every planner I tried eventually became one more thing I felt
              behind on. I wanted a system that expected the bad days, let me
              start again in two minutes, and never made me feel guilty for
              falling off. This is the tool I needed, so I built it for the rest
              of us too.
            </p>
            <a
              href="https://www.linkedin.com/in/tunde-sobowale-48658b14/"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 font-heading text-sm font-bold text-[#A8D5F5] hover:text-white"
              data-testid="founder-linkedin"
            >
              Connect with Tunde on LinkedIn <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </Section>

      <Section heading="Contact us" id="contact">
        <p>
          Have a question or need help? Send a message and we will reply to your
          email. You can also reach us at
          {" "}
          <a href="mailto:support@commandcenteradhd.com" className="font-semibold text-[#A8D5F5] hover:text-white">
            support@commandcenteradhd.com
          </a>
          .
        </p>
        <ContactForm />
      </Section>

      <Section heading="Explore more">
        <div className="grid gap-3 sm:grid-cols-3">
          <a href="/guides" className={linkCard} data-testid="about-setup-videos">
            <PlayCircle className="h-5 w-5 flex-shrink-0 text-[#F5A623]" />
            View Setup Videos
          </a>
          <a href="mailto:support@commandcenteradhd.com" className={linkCard} data-testid="about-contact-support">
            <MessageSquare className="h-5 w-5 flex-shrink-0 text-[#F5A623]" />
            Contact Support
          </a>
          <a href="/community" className={linkCard} data-testid="about-community">
            <Users className="h-5 w-5 flex-shrink-0 text-[#F5A623]" />
            Explore the Community
          </a>
        </div>
        <Link
          to="/"
          className="mt-4 inline-flex items-center gap-2 font-heading text-sm font-bold text-[#A8D5F5] hover:text-white"
          data-testid="about-home-link"
        >
          Back to home <ArrowRight className="h-4 w-4" />
        </Link>
      </Section>
    </PageShell>
  );
}
